/**
 * AudioManager utility for handling base64 audio playback with queuing system
 * Supports automatic playback, sequential queuing, and playback controls
 */

export interface AudioPlaybackOptions {
  volume?: number;
  autoPlay?: boolean;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

export interface AudioQueueItem {
  id: string;
  audioData: string; // base64 encoded audio
  options?: AudioPlaybackOptions;
}

export class AudioManager {
  private audioQueue: AudioQueueItem[] = [];
  private currentAudio: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private isPaused: boolean = false;
  private defaultVolume: number = 0.8;
  private audioCache: Map<string, string> = new Map(); // Cache for processed audio URLs
  private maxCacheSize: number = 50; // Limit cache size to prevent memory leaks
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Bind methods to preserve context
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.stop = this.stop.bind(this);
    this.queueAudio = this.queueAudio.bind(this);
    this.processQueue = this.processQueue.bind(this);
    this.cleanup = this.cleanup.bind(this);
    
    // Start periodic cleanup
    this.startPeriodicCleanup();
  }

  /**
   * Queue audio for sequential playback
   */
  public queueAudio(id: string, audioData: string, options?: AudioPlaybackOptions): void {
    if (!audioData || audioData.trim() === '') {
      console.warn('AudioManager: Empty audio data provided');
      return;
    }

    const queueItem: AudioQueueItem = {
      id,
      audioData,
      options: {
        volume: this.defaultVolume,
        autoPlay: true,
        ...options
      }
    };

    this.audioQueue.push(queueItem);

    // Start processing queue if not already playing
    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  /**
   * Process the audio queue sequentially
   */
  private async processQueue(): Promise<void> {
    if (this.audioQueue.length === 0 || this.isPlaying) {
      return;
    }

    const nextItem = this.audioQueue.shift();
    if (!nextItem) return;

    try {
      await this.playAudioItem(nextItem);
    } catch (error) {
      console.error('AudioManager: Error playing audio item:', error);
      if (nextItem.options?.onError) {
        nextItem.options.onError(error as Error);
      }
    }

    // Continue processing queue
    this.processQueue();
  }

  /**
   * Play a single audio item
   */
  private async playAudioItem(item: AudioQueueItem): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Create audio element from base64 data
        const audio = this.createAudioFromBase64(item.audioData);
        
        if (!audio) {
          reject(new Error('Failed to create audio element'));
          return;
        }

        this.currentAudio = audio;
        this.isPlaying = true;
        this.isPaused = false;

        // Set volume
        if (item.options?.volume !== undefined) {
          audio.volume = Math.max(0, Math.min(1, item.options.volume));
        }

        // Set up event listeners
        audio.onended = () => {
          this.isPlaying = false;
          this.currentAudio = null;
          if (item.options?.onEnd) {
            item.options.onEnd();
          }
          resolve();
        };

        audio.onerror = (event) => {
          this.isPlaying = false;
          this.currentAudio = null;
          const error = new Error(`Audio playback failed: ${event}`);
          if (item.options?.onError) {
            item.options.onError(error);
          }
          reject(error);
        };

        audio.onloadstart = () => {
          if (item.options?.onStart) {
            item.options.onStart();
          }
        };

        // Start playback
        if (item.options?.autoPlay !== false) {
          audio.play().catch((error) => {
            this.isPlaying = false;
            this.currentAudio = null;
            reject(new Error(`Failed to start audio playback: ${error.message}`));
          });
        }

      } catch (error) {
        this.isPlaying = false;
        this.currentAudio = null;
        reject(error);
      }
    });
  }

  /**
   * Create HTML audio element from base64 data with caching
   */
  private createAudioFromBase64(base64Data: string): HTMLAudioElement | null {
    try {
      // Check cache first
      const cacheKey = this.generateCacheKey(base64Data);
      let audioSrc = this.audioCache.get(cacheKey);

      if (!audioSrc) {
        // Handle data URL format or raw base64
        if (base64Data.startsWith('data:')) {
          audioSrc = base64Data;
        } else {
          // Assume it's WAV format as specified in requirements
          audioSrc = `data:audio/wav;base64,${base64Data}`;
        }

        // Add to cache with size limit
        this.addToCache(cacheKey, audioSrc);
      }

      const audio = new Audio(audioSrc);
      
      // Add cleanup listener to revoke object URLs when audio ends
      audio.addEventListener('ended', () => {
        this.cleanupAudioElement(audio);
      });

      return audio;
    } catch (error) {
      console.error('AudioManager: Failed to create audio from base64:', error);
      return null;
    }
  }

  /**
   * Generate cache key from audio data
   */
  private generateCacheKey(audioData: string): string {
    // Use a simple hash of the first and last 100 characters for cache key
    const start = audioData.substring(0, 100);
    const end = audioData.substring(Math.max(0, audioData.length - 100));
    return `${start.length}_${end.length}_${audioData.length}`;
  }

  /**
   * Add audio URL to cache with size management
   */
  private addToCache(key: string, audioSrc: string): void {
    // Remove oldest entries if cache is full
    if (this.audioCache.size >= this.maxCacheSize) {
      const firstKey = this.audioCache.keys().next().value;
      if (firstKey) {
        const oldSrc = this.audioCache.get(firstKey);
        if (oldSrc && oldSrc.startsWith('blob:')) {
          URL.revokeObjectURL(oldSrc);
        }
        this.audioCache.delete(firstKey);
      }
    }

    this.audioCache.set(key, audioSrc);
  }

  /**
   * Clean up audio element resources
   */
  private cleanupAudioElement(audio: HTMLAudioElement): void {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load(); // Reset the audio element
    } catch (error) {
      console.error('AudioManager: Error cleaning up audio element:', error);
    }
  }

  /**
   * Start periodic cleanup of unused resources
   */
  private startPeriodicCleanup(): void {
    this.cleanupInterval = setInterval(() => {
      this.performPeriodicCleanup();
    }, 60000); // Clean up every minute
  }

  /**
   * Perform periodic cleanup of resources
   */
  private performPeriodicCleanup(): void {
    // Clean up cache if it's getting too large
    if (this.audioCache.size > this.maxCacheSize * 0.8) {
      const keysToRemove = Array.from(this.audioCache.keys()).slice(0, Math.floor(this.maxCacheSize * 0.2));
      keysToRemove.forEach(key => {
        const audioSrc = this.audioCache.get(key);
        if (audioSrc && audioSrc.startsWith('blob:')) {
          URL.revokeObjectURL(audioSrc);
        }
        this.audioCache.delete(key);
      });
    }

    // Force garbage collection hint (if available)
    if (typeof window !== 'undefined' && 'gc' in window) {
      try {
        (window as any).gc();
      } catch (e) {
        // Ignore errors - gc() is not always available
      }
    }
  }

  /**
   * Play current audio (resume if paused)
   */
  public play(): void {
    if (this.currentAudio && this.isPaused) {
      this.currentAudio.play().catch((error) => {
        console.error('AudioManager: Failed to resume playback:', error);
      });
      this.isPaused = false;
    }
  }

  /**
   * Pause current audio playback
   */
  public pause(): void {
    if (this.currentAudio && this.isPlaying && !this.isPaused) {
      this.currentAudio.pause();
      this.isPaused = true;
    }
  }

  /**
   * Stop current audio and clear queue
   */
  public stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    
    this.isPlaying = false;
    this.isPaused = false;
    this.audioQueue = [];
  }

  /**
   * Get current playback state
   */
  public getPlaybackState(): {
    isPlaying: boolean;
    isPaused: boolean;
    queueLength: number;
    currentTime?: number;
    duration?: number;
  } {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      queueLength: this.audioQueue.length,
      currentTime: this.currentAudio?.currentTime,
      duration: this.currentAudio?.duration
    };
  }

  /**
   * Set default volume for new audio items
   */
  public setDefaultVolume(volume: number): void {
    this.defaultVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Clear the audio queue without stopping current playback
   */
  public clearQueue(): void {
    this.audioQueue = [];
  }

  /**
   * Get queue length
   */
  public getQueueLength(): number {
    return this.audioQueue.length;
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    this.stop();
    
    // Clean up current audio element
    if (this.currentAudio) {
      this.cleanupAudioElement(this.currentAudio);
      this.currentAudio = null;
    }

    // Clear periodic cleanup interval
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Clean up audio cache
    this.audioCache.forEach((audioSrc, key) => {
      if (audioSrc.startsWith('blob:')) {
        URL.revokeObjectURL(audioSrc);
      }
    });
    this.audioCache.clear();

    // Clear queue
    this.audioQueue = [];
  }
}

// Singleton instance for global use
export const audioManager = new AudioManager();